import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../redux/store';
import { closeHelpSidebar, openHelpSidebar } from '../redux/helpSlice';
import { FiX, FiInfo, FiZap, FiAlertCircle, FiExternalLink } from 'react-icons/fi';
import { manualSections } from './manual/manualData';
import type { ManualTip } from './manual/manualData';
import { Link } from 'react-router-dom';
import CustomSelect from './CustomSelect';

/* ── Tip Card ── */
const TipCard = ({ tip }: { tip: ManualTip }) => {
  const config = {
    tip: {
      bg: "bg-emerald-50", border: "border-emerald-200",
      icon: <FiZap className="text-emerald-500 shrink-0 mt-0.5" size={14} />,
      label: "Pro Tip", labelColor: "text-emerald-600", textColor: "text-emerald-800",
    },
    warning: {
      bg: "bg-amber-50", border: "border-amber-200",
      icon: <FiAlertCircle className="text-amber-500 shrink-0 mt-0.5" size={14} />,
      label: "Heads Up", labelColor: "text-amber-600", textColor: "text-amber-800",
    },
    info: {
      bg: "bg-blue-50", border: "border-blue-200",
      icon: <FiInfo className="text-blue-500 shrink-0 mt-0.5" size={14} />,
      label: "Good to Know", labelColor: "text-blue-600", textColor: "text-blue-800",
    },
  }[tip.type];

  return (
    <div className={`flex items-start gap-2.5 p-3.5 rounded-xl border ${config.bg} ${config.border} my-3`}>
      {config.icon}
      <div className="flex-1 min-w-0">
        <span className={`text-[10px] font-bold uppercase tracking-widest ${config.labelColor}`}>{config.label}</span>
        <p className={`text-xs leading-relaxed mt-0.5 ${config.textColor}`}>{tip.text}</p>
      </div>
    </div>
  );
};

export default function HelpSidebar() {
    const dispatch = useDispatch();
    const { isOpen, activeSectionId } = useSelector((state: RootState) => state.help);

    const section = manualSections.find(s => s.id === activeSectionId) || manualSections[0];

    // Handle escape key to close
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                dispatch(closeHelpSidebar());
            }
        };
        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, dispatch]);

    return (
        <>
            {/* Overlay */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[9990] transition-opacity"
                    onClick={() => dispatch(closeHelpSidebar())}
                />
            )}

            {/* Sidebar */}
            <div 
                className={`fixed top-0 right-0 h-full w-[400px] max-w-[90vw] bg-white shadow-2xl z-[9999] transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* Header */}
                <div className="p-4 border-b border-gray-100 shrink-0 bg-gray-50/90 backdrop-blur-sm space-y-3 relative z-20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-900">
                            <div className="p-2 bg-[#B08D55]/10 text-[#B08D55] rounded-lg">
                                <FiInfo size={18} />
                            </div>
                            <div>
                                <h2 className="font-bold text-base text-gray-900 leading-tight">Help Manual</h2>
                                <p className="text-[11px] text-gray-500">Contextual guide for current page</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => dispatch(closeHelpSidebar())}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                            aria-label="Close Help Sidebar"
                        >
                            <FiX size={20} />
                        </button>
                    </div>

                    {/* Topic Switcher */}
                    <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0">Topic:</span>
                        <div className="flex-1 min-w-0">
                            <CustomSelect
                                name="manualTopic"
                                value={section?.id || "dashboard"}
                                onChange={(_, val) => dispatch(openHelpSidebar(val))}
                                options={manualSections.map((s) => ({ value: s.id, label: s.title }))}
                                disableSearch={false}
                                className="!py-2 !px-3 !text-xs !rounded-xl !bg-white shadow-2xs"
                            />
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {section ? (
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 rounded-xl bg-gray-100 text-gray-700">
                                    <section.icon size={20} />
                                </div>
                                <h3 className="text-xl font-extrabold text-gray-900">{section.title}</h3>
                            </div>
                            
                            <p className="text-sm text-gray-600 leading-relaxed mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                {section.overview}
                            </p>

                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#B08D55]" />
                                        Step-by-Step Guide
                                    </h4>
                                    <div className="space-y-4">
                                        {section.steps.map((step, idx) => (
                                            <div key={idx} className="relative pl-4 border-l-2 border-[#B08D55]/20 hover:border-[#B08D55] transition-colors">
                                                <h5 className="text-xs font-bold text-gray-900 mb-1">{step.title}</h5>
                                                <p className="text-xs text-gray-600 leading-relaxed">{step.description}</p>
                                                {step.image && (
                                                    <div className="mt-3 rounded-xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50 p-1">
                                                        <img src={step.image} alt={step.title} className="w-full h-auto rounded-lg object-contain" />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {section.tips.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                            Helpful Tips
                                        </h4>
                                        <div>
                                            {section.tips.map((tip, idx) => (
                                                <TipCard key={idx} tip={tip} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                            <FiInfo size={40} className="mb-4 opacity-20" />
                            <p className="text-sm font-medium">Select a section to view its help guide.</p>
                        </div>
                    )}
                </div>

                {/* Footer Link */}
                {section && (
                    <div className="p-4 border-t border-gray-100 shrink-0 bg-gray-50">
                        <Link 
                            to="/manual" 
                            onClick={() => dispatch(closeHelpSidebar())}
                            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-[#B08D55] hover:border-[#B08D55]/40 transition-colors shadow-xs"
                        >
                            View Full Manual <FiExternalLink size={14} />
                        </Link>
                    </div>
                )}
            </div>
        </>
    );
}
